import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  dialogflowCompaniesQuery,
  dialogflowShellQuery,
  saveDialogflowSettings,
  type DialogflowForm,
} from '../../api/settingsEaseedeskAdamIntopModules'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Easeedesk -> Adam Intop -> DialogFlow Settings, element cu
 * element fata de `easeedesk_settings/adam_intop/dialogflow_settings.php`:
 * comutatorul de activare, cele trei liste cu bife (firme - aduse separat,
 * ca in macheta, din `getCompaniesDF` -, tipuri de tichet, surse de chat),
 * cele trei comutatoare de dedesubt si Save, cu aceleasi mesaje.
 */
const EMPTY: DialogflowForm = {
  status: false,
  connect_with_adam_analyser: false,
  can_make_ticket_changes: false,
  force_takeover: false,
  companies: [],
  ticket_types: [],
  chat_sources: [],
  ticket_sources: '',
}

function Switch({ name, label, checked, onChange }: { name: string; label: React.ReactNode; checked: boolean; onChange(on: boolean): void }) {
  return (
    <div className="row">
      <div className="col-md-12">
        <div className="form-group">
          <input type="checkbox" className="switchery_edit" name={name} checked={checked} onChange={(e) => onChange(e.target.checked)} />{' '}
          <label className="control-label">{label}</label>
        </div>
      </div>
    </div>
  )
}

export function AdamIntopDialogflowPage() {
  const shell = useQuery(dialogflowShellQuery())
  const companies = useQuery(dialogflowCompaniesQuery())
  const [form, setForm] = useState<DialogflowForm>(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!shell.data) return
    setForm({
      status: shell.data.status === 1,
      connect_with_adam_analyser: shell.data.connect_with_adam_analyser === 1,
      can_make_ticket_changes: shell.data.can_make_ticket_changes === 1,
      force_takeover: shell.data.force_takeover === 1,
      companies: shell.data.selected_companies.map(Number),
      ticket_types: shell.data.selected_ticket_types.map(Number),
      chat_sources: shell.data.selected_chat_ticket_sources,
      ticket_sources: '',
    })
  }, [shell.data])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveDialogflowSettings(form)
      .then((answer) => {
        setBusy(false)
        if (answer.ok) toast('Successfully saved.', 'Success')
        else toast('The Dialogflow settings could not be saved.', 'Error', 'error')
      })
      .catch(() => {
        setBusy(false)
        toast('Something went wrong.', 'Error', 'error')
      })
  }

  const set = (patch: Partial<DialogflowForm>) => setForm({ ...form, ...patch })

  return (
    <div>
      <h4>
        <strong>DialogFlow</strong> Settings [for <strong> {shell.data.providerName} </strong> SP]
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>DialogFlow settings</strong> for Adam Intop
      </label>
      <hr />

      <form id="save-adam-df-intop-form" onSubmit={submit}>
        <Switch name="status" label={<strong> Activate DialogFlow </strong>} checked={form.status} onChange={(on) => set({ status: on })} />

        <div className="clearfix"></div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Select Companies (multiple selection)</label>
              <SelectPicker
                id="assigned_companies"
                name="companies[]"
                className="form-control"
                title="Nothing selected"
                multiple
                selectedTextFormat="count > 1"
                size={10}
                options={(companies.data?.companies ?? []).map((c) => ({ value: String(c.ID), label: c.name }))}
                value={form.companies.map(String)}
                onChange={(v) => set({ companies: v.map(Number) })}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Ticket Types (multiple selection)</label>
              <SelectPicker
                id="run_only_on_ticket_types"
                name="ticket_types[]"
                className="form-control"
                title="Nothing selected"
                multiple
                selectedTextFormat="count > 1"
                size={10}
                options={shell.data.all_ticket_types.map((t) => ({ value: String(t.ID), label: `${t.short_name} (${t.type_name})` }))}
                value={form.ticket_types.map(String)}
                onChange={(v) => set({ ticket_types: v.map(Number) })}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Chat Sources (multiple selection)</label>
              <SelectPicker
                id="run_only_on_chat_sources"
                name="chat_sources[]"
                className="form-control"
                title="Nothing selected"
                multiple
                selectedTextFormat="count > 1"
                size={10}
                options={shell.data.chat_ticket_sources.map((s) => ({ value: s.chat_source, label: s.source_name }))}
                value={form.chat_sources}
                onChange={(v) => set({ chat_sources: v })}
              />
            </div>
          </div>
        </div>

        <Switch
          name="connect_with_adam_analyser"
          label={<strong>Connect With Adam Analyser </strong>}
          checked={form.connect_with_adam_analyser}
          onChange={(on) => set({ connect_with_adam_analyser: on })}
        />
        <Switch
          name="can_make_ticket_changes"
          label={<strong>Can Make Ticket Changes </strong>}
          checked={form.can_make_ticket_changes}
          onChange={(on) => set({ can_make_ticket_changes: on })}
        />
        <Switch
          name="force_takeover"
          label={
            <>
              <strong>Force TakeOver</strong> (Adam can takeover tickets even if Auto Assign is enabled for technicians)
            </>
          }
          checked={form.force_takeover}
          onChange={(on) => set({ force_takeover: on })}
        />

        <hr className="dotted" />

        <div className="col-md-12 center-block">
          <button id="save-df-settings" type="submit" className="btn btn-success center-block" disabled={busy}>
            Save
          </button>
        </div>
      </form>
    </div>
  )
}
